import { ArrowLeftOutlined } from '@ant-design/icons';
import { type ProColumns, ProTable } from '@ant-design/pro-components';
import {
  App,
  Button,
  DatePicker,
  Descriptions,
  Empty,
  Form,
  Input,
  Modal,
  Select,
  Skeleton,
  Space,
  Tag,
  Timeline,
} from 'antd';
import React from 'react';
import {
  DetailInspector,
  DetailSection,
  type DetailSectionItem,
  DetailSummary,
} from '@/components/DetailInspector';
import { useIsNarrow } from '@/hooks/useIsNarrow';
import { ArchivePanel } from '@/pages/Enterprise360/EnterpriseArchivePanels';
import EnterpriseFiles from '@/pages/EnterpriseLibrary/EnterpriseFiles';
import EnterpriseInvestmentOrders from '@/pages/EnterpriseLibrary/InvestmentOrders';
import {
  CLAIM_TEXT,
  displayText,
  enterpriseStatusText,
  INVITATION_TEXT,
  SOURCE_TEXT,
  STAGE_TEXT,
} from '@/pages/EnterpriseLibrary/labels';
import { parkApi } from '@/services/bff/park.api';
import type {
  EnterpriseLibraryClaimHistory,
  EnterpriseLibraryDetail,
  EnterpriseLibraryExitRecord,
} from '@/services/bff/types/enterprise-library';
import type {
  AdmissionApplication,
  ParkLease,
  ServiceOrder,
} from '@/services/bff/types/park';
import { businessTableColumns } from '@/utils/businessTableColumns';
import { formatDate, formatDateTime } from '@/utils/datetime';
import styles from './index.module.less';

const sections = [
  { key: 'overview', label: '企业概览' },
  { key: 'relation', label: '园企关系' },
  { key: 'investment-orders', label: '招商订单' },
  { key: 'claims', label: '认领记录' },
  { key: 'admissions', label: '入驻记录' },
  { key: 'exits', label: '迁出记录' },
  { key: 'leases', label: '租赁合同' },
  { key: 'orders', label: '服务订单' },
  { key: 'archives', label: '企业档案' },
];

const formatArea = (area?: number | null) =>
  area == null ? '-' : `${Number(area).toFixed(2)} ㎡`;

const enterpriseTypeText: Record<string, string> = {
  resident: '入驻企业',
  incubating: '孵化企业',
  registered: '注册企业',
  talent: '人才',
};

const stageColor: Record<string, string> = {
  prospect: 'default',
  applying: 'processing',
  pending_entry: 'warning',
  active: 'success',
  exiting: 'warning',
  exited: 'default',
};

const claimColor: Record<string, string> = {
  unclaimed: 'default',
  pending_review: 'warning',
  claimed: 'success',
};

const admissionStatusText: Record<string, string> = {
  submitted: '已提交',
  reviewing: '审核中',
  approved: '已通过',
  rejected: '已驳回',
  withdrawn: '已撤回',
};

const exitStatusText: Record<string, string> = {
  draft: '草稿',
  pending: '审批中',
  approved: '已通过',
  rejected: '已驳回',
  withdrawn: '已撤回',
  completed: '已完成',
};

const leaseStatusText: Record<ParkLease['status'], string> = {
  draft: '草稿',
  active: '生效中',
  expired: '已到期',
  terminated: '已终止',
};

const leaseStatusColor: Record<ParkLease['status'], string> = {
  draft: 'default',
  active: 'success',
  expired: 'warning',
  terminated: 'error',
};

const orderStatusText: Record<ServiceOrder['status'], string> = {
  draft: '草稿',
  pending_confirmation: '待确认',
  confirmed: '待合同',
  in_progress: '履约中',
  pending_acceptance: '待验收',
  completed: '已完成',
  cancelled: '异常',
};

const orderStatusColor: Record<ServiceOrder['status'], string> = {
  draft: 'default',
  pending_confirmation: 'processing',
  confirmed: 'processing',
  in_progress: 'processing',
  pending_acceptance: 'warning',
  completed: 'success',
  cancelled: 'error',
};

const formatAmount = (amount?: number | null) =>
  amount == null ? '-' : `${Number(amount).toFixed(2)} 元`;

const claimRecordText: Record<string, string> = {
  pending: '待审核',
  approved: '已通过',
  rejected: '已驳回',
  cancelled: '已取消',
};

/**
 * 企业详情内容：运行在全局详情弹窗内，不依赖路由。
 */
const EnterpriseDetailPanel: React.FC<{
  parkEnterpriseUuid: string;
  initialTab?: string;
  onRequestClose?: () => void;
}> = ({ parkEnterpriseUuid, initialTab = 'overview', onRequestClose }) => {
  const { message } = App.useApp();
  const [tab, setTab] = React.useState(
    sections.some((item) => item.key === initialTab) ? initialTab : 'overview',
  );
  const [detail, setDetail] = React.useState<EnterpriseLibraryDetail>();
  const [claims, setClaims] = React.useState<EnterpriseLibraryClaimHistory>();
  const [admissions, setAdmissions] = React.useState<AdmissionApplication[]>(
    [],
  );
  const [exits, setExits] = React.useState<EnterpriseLibraryExitRecord[]>([]);
  const [tabLeases, setTabLeases] = React.useState<ParkLease[]>([]);
  const [tabOrders, setTabOrders] = React.useState<ServiceOrder[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [loadFailed, setLoadFailed] = React.useState(false);
  const descriptionColumn = useIsNarrow(600) ? 1 : 2;
  const requestSeq = React.useRef(0);

  const load = React.useCallback(async () => {
    if (!parkEnterpriseUuid) return;
    const seq = ++requestSeq.current;
    setLoading(true);
    setLoadFailed(false);
    try {
      const data = await parkApi.getEnterpriseLibraryItem(parkEnterpriseUuid);
      if (seq !== requestSeq.current) return;
      setDetail(data);
    } catch {
      if (seq !== requestSeq.current) return;
      setDetail(undefined);
      setLoadFailed(true);
      message.error('企业详情加载失败');
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [parkEnterpriseUuid]);

  React.useEffect(() => {
    void load();
  }, [load]);

  React.useEffect(() => {
    if (!parkEnterpriseUuid) return;
    const seq = requestSeq.current;
    const run = async () => {
      try {
        if (tab === 'claims') {
          const data =
            await parkApi.listEnterpriseLibraryClaims(parkEnterpriseUuid);
          if (seq !== requestSeq.current) return;
          setClaims(data);
        }
        if (tab === 'admissions') {
          const data =
            await parkApi.listEnterpriseLibraryAdmissions(parkEnterpriseUuid);
          if (seq !== requestSeq.current) return;
          setAdmissions(data);
        }
        if (tab === 'exits') {
          const data =
            await parkApi.listEnterpriseLibraryExits(parkEnterpriseUuid);
          if (seq !== requestSeq.current) return;
          setExits(data);
        }
        if (tab === 'leases') {
          // 企业库详情只有企业主体标识，租赁按企业主体查询
          const enterpriseTenantUuid = detail?.enterpriseUuid;
          if (!enterpriseTenantUuid) {
            setTabLeases([]);
            return;
          }
          const data = await parkApi.listEnterpriseLeases(
            enterpriseTenantUuid,
            { pageNumber: 1, pageSize: 100 },
          );
          if (seq !== requestSeq.current) return;
          setTabLeases(data.items);
        }
        if (tab === 'orders') {
          const enterpriseTenantUuid = detail?.enterpriseUuid;
          if (!enterpriseTenantUuid) {
            setTabOrders([]);
            return;
          }
          const data = await parkApi.listOrders({
            enterpriseTenantUuid,
            pageNumber: 1,
            pageSize: 100,
          });
          if (seq !== requestSeq.current) return;
          setTabOrders(data.items);
        }
      } catch {
        if (seq !== requestSeq.current) return;
        message.error('记录加载失败');
      }
    };
    void run();
  }, [parkEnterpriseUuid, tab, detail?.enterpriseUuid]);

  const [entryModalOpen, setEntryModalOpen] = React.useState(false);
  const [leases, setLeases] = React.useState<ParkLease[]>([]);
  const [entrySubmitting, setEntrySubmitting] = React.useState(false);
  const [entryForm] = Form.useForm();

  const handleOpenEntryModal = async () => {
    try {
      const leaseList = await parkApi.listLeases({
        status: 'active',
        pageSize: 50,
      });
      const matched = leaseList.items.filter(
        (l) =>
          !l.parkEnterpriseUuid || l.parkEnterpriseUuid === parkEnterpriseUuid,
      );
      setLeases(matched);
      entryForm.setFieldsValue({
        contactName: detail?.contactName,
        contactMobile: detail?.contactMobile,
      });
      setEntryModalOpen(true);
    } catch {
      message.error('加载可用租赁记录失败');
    }
  };

  const handleCreateEntry = async () => {
    const values = await entryForm.validateFields();
    setEntrySubmitting(true);
    try {
      await parkApi.createEnterpriseEntry({
        parkEnterpriseUuid,
        leaseUuid: values.leaseUuid,
        plannedEntryDate: values.plannedEntryDate?.format('YYYY-MM-DD'),
        contactName: values.contactName,
        contactMobile: values.contactMobile,
        remark: values.remark,
      });
      message.success('入驻办理单发起成功！');
      setEntryModalOpen(false);
      entryForm.resetFields();
      onRequestClose?.();
      window.location.assign('/enterprises/entries');
    } catch (err: any) {
      message.error(err?.message || '发起入驻单失败');
    } finally {
      setEntrySubmitting(false);
    }
  };

  if (loading && !detail) {
    return (
      <div className={styles.state}>
        <Skeleton active paragraph={{ rows: 10 }} />
      </div>
    );
  }

  if (!detail) {
    return (
      <div className={styles.state}>
        <Empty
          description={
            loadFailed ? '企业详情加载失败，请稍后重试' : '企业不存在或已被删除'
          }
        />
        <Space>
          {loadFailed ? (
            <Button onClick={() => void load()}>重试</Button>
          ) : null}
          {onRequestClose ? (
            <Button icon={<ArrowLeftOutlined />} onClick={onRequestClose}>
              关闭
            </Button>
          ) : null}
        </Space>
      </div>
    );
  }

  const claimCount = claims
    ? claims.invitations.length +
      claims.claims.length +
      (claims.unclaims?.length || 0)
    : undefined;

  const items: DetailSectionItem[] = [
    { key: 'overview', label: '企业概览' },
    { key: 'relation', label: '园企关系' },
    { key: 'investment-orders', label: '招商订单' },
    { key: 'claims', label: '认领记录', count: claimCount },
    { key: 'admissions', label: '入驻记录', count: admissions.length },
    { key: 'exits', label: '迁出记录', count: exits.length },
    { key: 'leases', label: '租赁合同', count: tabLeases.length },
    { key: 'orders', label: '服务订单', count: tabOrders.length },
    { key: 'archives', label: '企业档案' },
  ];

  return (
    <div className={styles.body}>
      <DetailInspector
        header={
          <DetailSummary
            title={detail.name}
            tags={
              <>
                <Tag color={stageColor[detail.stage] || 'default'}>
                  {enterpriseStatusText(detail.stage, detail.enterpriseType)}
                </Tag>
                <Tag>{enterpriseTypeText[detail.enterpriseType]}</Tag>
                <Tag color={claimColor[detail.claimStatus] || 'default'}>
                  {CLAIM_TEXT[detail.claimStatus]}
                </Tag>
                {detail.profileIncomplete ? (
                  <Tag color="gold">资料待补齐</Tag>
                ) : null}
              </>
            }
            context={`${displayText(detail.industry)} · ${displayText(
              detail.address,
            )}`}
            facts={[
              { label: '注册面积', value: formatArea(detail.registrationArea) },
              { label: '租赁面积', value: formatArea(detail.leaseArea) },
              { label: '入驻时间', value: formatDate(detail.joinedAt) },
              { label: '数据来源', value: SOURCE_TEXT[detail.source] || '-' },
            ]}
            actions={
              <Button
                type="primary"
                onClick={() => void handleOpenEntryModal()}
              >
                发起企业入驻
              </Button>
            }
          />
        }
        sections={items}
        activeKey={tab}
        onChange={setTab}
        ariaLabel="企业详情分区"
      >
        {tab === 'overview' ? (
          <>
            <DetailSection title="基本信息" hint="工商与联系信息">
              <Descriptions column={descriptionColumn} size="small">
                <Descriptions.Item label="企业名称">
                  {detail.name}
                </Descriptions.Item>
                <Descriptions.Item label="统一社会信用代码">
                  {detail.profileIncomplete ||
                  !detail.unifiedSocialCreditCode ||
                  detail.unifiedSocialCreditCode === '-' ? (
                    <Tag color="gold">待补齐</Tag>
                  ) : (
                    detail.unifiedSocialCreditCode
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="行业方向">
                  {displayText(detail.industry)}
                </Descriptions.Item>
                <Descriptions.Item label="企业类型">
                  {enterpriseTypeText[detail.enterpriseType] || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="企业状态">
                  <Tag color={stageColor[detail.stage] || 'default'}>
                    {enterpriseStatusText(detail.stage, detail.enterpriseType)}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="认领状态">
                  <Tag color={claimColor[detail.claimStatus] || 'default'}>
                    {CLAIM_TEXT[detail.claimStatus]}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="注册面积">
                  {formatArea(detail.registrationArea)}
                </Descriptions.Item>
                <Descriptions.Item label="租赁面积">
                  {formatArea(detail.leaseArea)}
                </Descriptions.Item>
                <Descriptions.Item label="法人">
                  {displayText(detail.legalRepresentative)}
                </Descriptions.Item>
                <Descriptions.Item label="联系方式">
                  {displayText(detail.contactMobile)}
                </Descriptions.Item>
                <Descriptions.Item label="企业地址" span={2}>
                  {displayText(detail.address)}
                </Descriptions.Item>
                <Descriptions.Item label="来源">
                  {SOURCE_TEXT[detail.source] || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="入驻时间">
                  {formatDate(detail.joinedAt)}
                </Descriptions.Item>
                <Descriptions.Item label="迁出时间">
                  {formatDate(detail.exitedAt)}
                </Descriptions.Item>
                <Descriptions.Item label="备注" span={2}>
                  {displayText(detail.remark)}
                </Descriptions.Item>
              </Descriptions>
            </DetailSection>
            <DetailSection title="企业展示文件" hint="对外展示素材">
              <EnterpriseFiles value={detail.displayImageUuids || []} />
            </DetailSection>
            <DetailSection title="企业证照文件" hint="营业执照等">
              <EnterpriseFiles value={detail.certificateImageUuids || []} />
            </DetailSection>
            <DetailSection title="法人证照文件" hint="法人身份证明">
              <EnterpriseFiles
                value={detail.legalRepresentativeImageUuids || []}
              />
            </DetailSection>
          </>
        ) : null}

        {tab === 'relation' ? (
          <DetailSection title="园企关系" hint="当前园区的绑定与资格">
            <Descriptions column={descriptionColumn} size="small">
              <Descriptions.Item label="园区">
                {detail.relation.parkName}
              </Descriptions.Item>
              <Descriptions.Item label="企业状态">
                {STAGE_TEXT[detail.relation.stage]}
              </Descriptions.Item>
              <Descriptions.Item label="认领状态">
                {CLAIM_TEXT[detail.relation.claimStatus]}
              </Descriptions.Item>
              <Descriptions.Item label="智阅企业绑定">
                {detail.relation.tenantBound ? '已绑定' : '未绑定'}
              </Descriptions.Item>
              <Descriptions.Item label="访问资格">
                {detail.relation.membershipStatus}
              </Descriptions.Item>
              <Descriptions.Item label="最新邀请状态">
                {detail.relation.latestInvitationStatus
                  ? INVITATION_TEXT[detail.relation.latestInvitationStatus]
                  : '暂无邀请'}
              </Descriptions.Item>
            </Descriptions>
          </DetailSection>
        ) : null}

        {tab === 'claims' ? (
          <DetailSection title="认领记录" hint="邀请、认领与解除记录">
            {!claims?.invitations.length &&
            !claims?.claims.length &&
            !claims?.unclaims?.length ? (
              <Empty description="暂无认领记录" />
            ) : (
              <Timeline
                items={[
                  ...(claims?.unclaims || []).map((item) => ({
                    children: (
                      <div>
                        <div>解除当前园区认领</div>
                        <div>操作人 {displayText(item.operatedBy)}</div>
                        <div>操作时间 {formatDateTime(item.operatedAt)}</div>
                        {item.reason ? <div>原因 {item.reason}</div> : null}
                      </div>
                    ),
                  })),
                  ...(claims?.claims || []).map((item) => ({
                    children: (
                      <div>
                        <div>
                          认领申请 {claimRecordText[item.status] || item.status}
                        </div>
                        <div>
                          当前认领状态{' '}
                          {claims?.claimStatus
                            ? CLAIM_TEXT[claims.claimStatus]
                            : '-'}
                        </div>
                        <div>提交人 {displayText(item.applicantUserUuid)}</div>
                        <div>提交时间 {formatDateTime(item.submittedAt)}</div>
                        <div>审核人 {displayText(item.reviewedBy)}</div>
                        <div>审核时间 {formatDateTime(item.reviewedAt)}</div>
                        {item.rejectReason ? (
                          <div>驳回原因 {item.rejectReason}</div>
                        ) : null}
                      </div>
                    ),
                  })),
                  ...(claims?.invitations || []).map((item) => ({
                    children: (
                      <div>
                        <div>
                          邀请 {INVITATION_TEXT[item.status] || item.status}
                        </div>
                        <div>创建时间 {formatDateTime(item.createdAt)}</div>
                      </div>
                    ),
                  })),
                ]}
              />
            )}
          </DetailSection>
        ) : null}

        {tab === 'admissions' ? (
          <DetailSection title="入驻记录" hint="入驻办理单">
            <ProTable
              scroll={{ x: 625 }}
              tableLayout="fixed"
              cardBordered={false}
              search={false}
              options={false}
              toolBarRender={false}
              pagination={false}
              rowKey="uuid"
              dataSource={admissions}
              locale={{ emptyText: '暂无入驻记录' }}
              columns={businessTableColumns([
                {
                  ellipsis: true,
                  title: '企业名称',
                  dataIndex: 'enterpriseName',
                },
                { width: 140, title: '申请类型', dataIndex: 'applicationType' },
                {
                  width: 110,
                  title: '状态',
                  dataIndex: 'status',
                  valueEnum: Object.fromEntries(
                    Object.entries(admissionStatusText).map(
                      ([value, label]) => [value, { text: label }],
                    ),
                  ),
                  render: (value: any) => admissionStatusText[value] || '-',
                },
                {
                  width: 175,
                  title: '提交时间',
                  dataIndex: 'createdAt',
                  render: (value: any) => formatDateTime(value),
                },
              ])}
            />
          </DetailSection>
        ) : null}

        {tab === 'exits' ? (
          <DetailSection title="迁出记录" hint="迁出办理与结果">
            <ProTable<EnterpriseLibraryExitRecord>
              scroll={{ x: 825 }}
              tableLayout="fixed"
              cardBordered={false}
              search={false}
              options={false}
              toolBarRender={false}
              pagination={false}
              rowKey="uuid"
              dataSource={exits}
              locale={{ emptyText: '暂无迁出记录' }}
              columns={businessTableColumns([
                {
                  width: 120,
                  title: '当前状态',
                  dataIndex: 'status',
                  valueEnum: Object.fromEntries(
                    Object.entries(exitStatusText).map(([value, label]) => [
                      value,
                      { text: label },
                    ]),
                  ),
                  render: (value: any) => exitStatusText[value] || '-',
                },
                {
                  width: 145,
                  title: '计划迁出日期',
                  dataIndex: 'plannedExitDate',
                  render: (value: any) => formatDate(value),
                },
                {
                  width: 120,
                  title: '审批状态',
                  dataIndex: 'workflowStatus',
                  render: (value: any) => value || '-',
                },
                {
                  width: 145,
                  title: '实际迁出日期',
                  dataIndex: 'actualExitDate',
                  render: (value: any) => formatDate(value),
                },
                {
                  width: 120,
                  title: '最终结果',
                  dataIndex: 'status',
                  valueEnum: Object.fromEntries(
                    Object.entries(exitStatusText).map(([value, label]) => [
                      value,
                      { text: label },
                    ]),
                  ),
                  render: (value: any) => exitStatusText[value] || '-',
                },
                {
                  width: 175,
                  title: '申请时间',
                  dataIndex: 'submittedAt',
                  render: (value: any) => formatDateTime(value),
                },
              ])}
            />
          </DetailSection>
        ) : null}

        {tab === 'leases' ? (
          <DetailSection
            title="租赁合同"
            hint="该企业在本园区签署的全部租赁合同"
          >
            <ProTable<ParkLease>
              rowKey="uuid"
              search={false}
              options={false}
              pagination={{ pageSize: 10, showSizeChanger: false }}
              dataSource={tabLeases}
              locale={{ emptyText: '该企业暂无租赁合同' }}
              columns={businessTableColumns<ProColumns<ParkLease>>([
                {
                  title: '合同编号',
                  dataIndex: 'leaseNumber',
                  width: 160,
                  ellipsis: true,
                  render: (_, row) => row.leaseNumber || '-',
                },
                {
                  title: '合同名称',
                  dataIndex: 'contractName',
                  ellipsis: true,
                  render: (_, row) => row.contractName || '-',
                },
                {
                  title: '房源',
                  dataIndex: 'spaceName',
                  width: 180,
                  ellipsis: true,
                  render: (_, row) => row.spacePath || row.spaceName || '-',
                },
                {
                  title: '租赁面积',
                  dataIndex: 'leaseArea',
                  width: 110,
                  align: 'right',
                  render: (_, row) => formatArea(row.leaseArea),
                },
                {
                  title: '租金',
                  dataIndex: 'rentAmount',
                  width: 120,
                  align: 'right',
                  render: (_, row) => formatAmount(row.rentAmount),
                },
                {
                  title: '租赁期',
                  key: 'period',
                  width: 200,
                  render: (_, row) =>
                    `${formatDate(row.startDate)} 至 ${formatDate(row.endDate)}`,
                },
                {
                  title: '状态',
                  dataIndex: 'status',
                  width: 100,
                  render: (_, row) => (
                    <Tag color={leaseStatusColor[row.status]}>
                      {leaseStatusText[row.status] || row.status}
                    </Tag>
                  ),
                },
              ])}
            />
          </DetailSection>
        ) : null}

        {tab === 'investment-orders' ? (
          <EnterpriseInvestmentOrders parkEnterpriseUuid={parkEnterpriseUuid} />
        ) : null}

        {tab === 'orders' ? (
          <DetailSection
            title="服务订单"
            hint="该企业在本园区签订的全部服务订单"
          >
            <ProTable<ServiceOrder>
              rowKey="uuid"
              search={false}
              options={false}
              pagination={{ pageSize: 10, showSizeChanger: false }}
              dataSource={tabOrders}
              locale={{ emptyText: '该企业暂无服务订单' }}
              columns={businessTableColumns<ProColumns<ServiceOrder>>([
                {
                  title: '服务名称',
                  dataIndex: 'serviceNameSnapshot',
                  ellipsis: true,
                  render: (_, row) => row.serviceNameSnapshot || '-',
                },
                {
                  title: '数量',
                  dataIndex: 'quantity',
                  width: 90,
                  align: 'right',
                  render: (_, row) => row.quantity ?? '-',
                },
                {
                  title: '金额',
                  dataIndex: 'amount',
                  width: 130,
                  align: 'right',
                  render: (_, row) => formatAmount(row.amount),
                },
                {
                  title: '状态',
                  dataIndex: 'status',
                  width: 110,
                  render: (_, row) => (
                    <Tag color={orderStatusColor[row.status]}>
                      {orderStatusText[row.status] || row.status}
                    </Tag>
                  ),
                },
                {
                  title: '下单时间',
                  dataIndex: 'orderedAt',
                  width: 170,
                  render: (_, row) =>
                    row.orderedAt ? formatDateTime(row.orderedAt) : '-',
                },
                {
                  title: '创建时间',
                  dataIndex: 'createdAt',
                  width: 170,
                  render: (_, row) => formatDateTime(row.createdAt),
                },
              ])}
            />
          </DetailSection>
        ) : null}

        {tab === 'archives' ? (
          <>
            <div className={styles.archiveBlock}>
              <ArchivePanel
                fitColumns
                parkEnterpriseUuid={parkEnterpriseUuid}
                kind="qualifications"
                readOnly={false}
              />
            </div>
            <div className={styles.archiveBlock}>
              <ArchivePanel
                fitColumns
                parkEnterpriseUuid={parkEnterpriseUuid}
                kind="intellectual-properties"
                readOnly={false}
              />
            </div>
          </>
        ) : null}
      </DetailInspector>

      <Modal
        rootClassName="platform-ui"
        title="发起企业入驻办理"
        open={entryModalOpen}
        onCancel={() => setEntryModalOpen(false)}
        onOk={() => void handleCreateEntry()}
        confirmLoading={entrySubmitting}
        width="min(720px, calc(100vw - 48px))"
        centered
        destroyOnHidden
        keyboard
        closable={{ 'aria-label': '关闭' }}
        styles={{
          body: { maxHeight: 'calc(100dvh - 200px)', overflowY: 'auto' },
        }}
      >
        <Form form={entryForm} layout="vertical">
          <Form.Item
            name="leaseUuid"
            label="关联有效租赁"
            rules={[{ required: true, message: '请选择关联的有效租赁合同' }]}
          >
            <Select
              placeholder="请选择"
              options={leases.map((l) => ({
                label: `${l.contractName || '租赁合同'} (${l.leaseNumber}) - 空间: ${l.spaceName || l.spaceUuid}`,
                value: l.uuid,
              }))}
            />
          </Form.Item>
          <Form.Item name="plannedEntryDate" label="计划入驻日期">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="contactName" label="联系人姓名">
            <Input />
          </Form.Item>
          <Form.Item name="contactMobile" label="联系人电话">
            <Input />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EnterpriseDetailPanel;
